import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';
import teensWebP from '@/assets/teens.webp';
import teensJPG from '@/assets/teens.jpg';
import youngAdultWebP from '@/assets/young_adult.webp';
import youngAdultJPG from '@/assets/young_adult.jpg';
import familyWebP from '@/assets/family.webp';
import familyJPG from '@/assets/family.jpg';
import postpartumWebP from '@/assets/postpartum.webp';
import postpartumJPG from '@/assets/postpartum.jpg';
import treatmentWebP from '@/assets/treatment.webp';
import treatmentJPG from '@/assets/treatment.jpg';
import menopauseWebP from '@/assets/menopause.webp';
import menopauseJPG from '@/assets/menopause.jpg';

interface LifeStage {
  stage: string;
  id: string;
  description: string;
  imageWebP: string;
  imageJPG: string;
  alt: string;
}

const LifeStageSection: React.FC = () => {
  const lifeStages: LifeStage[] = [
    {
      stage: "Teens",
      id: "teens",
      description: "Elevate your child's well-being with the time-tested wisdom of Acupuncture and Chinese Medicine! Our specialised treatments blend ancient traditions with modern insights to address the unique needs of teens. From managing stress and hormonal changes to boosting energy and enhancing focus, we offer tailored solutions to support their physical, mental, and emotional health. Our experienced practitioners provide gentle acupuncture sessions and expert guidance in Chinese herbal medicine, empowering young teens to thrive naturally.",
      imageWebP: teensWebP,
      imageJPG: teensJPG,
      alt: "Acupuncture and Chinese medicine for teenage hormonal health on the Sunshine Coast"
    },
    {
      stage: "Young Adults",
      id: "young-adults",
      description: "Finished Uni? wanting to buy a house? maybe travel..? not really sure??!? just having fun? or.. feeling stressed in a flux or overwhelmed with it all? well we're here to help! We have been down this path also and are here to help you navigate life's changes with grace and vitality through the transformative power of Chinese Medicine and Acupuncture! Our specialised treatments offer natural solutions for managing cycles and hormonal fluctuations, helping young adults achieve balance and well-being. Whether facing menstrual irregularities, PMS symptoms, or hormonal imbalances, our expert practitioners provide personalised care tailored to individual needs.",
      imageWebP: youngAdultWebP,
      imageJPG: youngAdultJPG,
      alt: "Natural hormone and stress support for young women in Currimundi"
    },
    {
      stage: "Pregnancy",
      id: "pregnancy",
      description: "At Women and The Way, we understand the delicate balance of your body during this transformative time, and our experienced practitioners are here to support you every step of the way. Through the wisdom of Chinese Medicine, we offer gentle yet effective treatments tailored to address the unique needs of pregnancy. From alleviating common discomforts like morning sickness, fatigue, and back pain, to promoting optimal fetal positioning and preparing for labor, our comprehensive approach aims to enhance your overall well-being and optimize your pregnancy experience.",
      imageWebP: familyWebP,
      imageJPG: familyJPG,
      alt: "Pregnancy acupuncture and care with Chinese medicine in Caloundra"
    },
    {
      stage: "Postpartum",
      id: "postpartum",
      description: "Pregnancy and childbirth leave the body depleted. Women and the Way practitioners are here to help you restore your blood, energy, vitality, and hormones. At Women and the Way, experience personalised postpartum care tailored to your needs. Our expert team utilizes gentle acupuncture, Chinese herbs, and personalised diet and lifestyle advice to address hormonal balance, fatigue, breastfeeding challenges, emotional well-being, pelvic health, and digestive issues. Restore vitality and resilience in a nurturing environment, or if you have little ones and travel is too much then let us come to you and do the healing from the comfort of your own home.",
      imageWebP: postpartumWebP,
      imageJPG: postpartumJPG,
      alt: "Postpartum recovery support with acupuncture and herbs in Currimundi"
    },
    {
      stage: "Perimenopause",
      id: "perimenopause",
      description: "Entering perimenopause can bring about a myriad of changes in your body, leaving you feeling depleted and unbalanced. However, at Women and the Way, our practitioners are dedicated to helping you navigate this transition with ease. Whether you're experiencing hormonal fluctuations, heavy or inconsistent periods, mood swings, hot flashes, or disrupted sleep, our specialised care is tailored to address your unique needs. With a combination of acupuncture, Chinese herbs, and personalised lifestyle advice, we aim to restore harmony to your body and mind during this transformative time.",
      imageWebP: treatmentWebP,
      imageJPG: treatmentJPG,
      alt: "Perimenopause care and hormone balance with acupuncture on the Sunshine Coast"
    },
    {
      stage: "Menopause",
      id: "menopause",
      description: "Menopause can be a scary time where your body might stop functioning like it used to. Many women experience hot flushes, night sweats, mood swings, dryness and weight gain. Let us go on this journey with you. Chinese Medicine and Acupuncture could help regulate your hormones, reduce hot flushes, improve your mood and have you feeling yourself again.",
      imageWebP: menopauseWebP,
      imageJPG: menopauseJPG,
      alt: "Chinese medicine for menopause symptoms and vitality in Caloundra"
    }
  ];

  return (
    <section className="section-padding bg-lavender-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="mb-4 bg-gradient-to-r from-sage-600 to-amber-500 bg-clip-text text-transparent">
            Supporting Every Life Stage
          </h2>
          <p className="max-w-3xl mx-auto text-lg text-gray-700 mb-4">
            Our specialised care adapts to your unique needs throughout life's transformative phases.
          </p>
          <p className="max-w-2xl mx-auto text-base text-gray-600">
            From the first hormonal changes of adolescence to the wisdom of menopause, we provide gentle, evidence-based care that honours your body's natural rhythms and supports your wellbeing at every stage of life.
          </p>
        </div>

        <Tabs defaultValue="teens" className="w-full">
          <TabsList className="grid grid-cols-3 md:grid-cols-6 mb-8">
            {lifeStages.map((stage) => (
              <TabsTrigger
                key={stage.id}
                value={stage.id}
                className="data-[state=active]:bg-sage-100 data-[state=active]:text-sage-800 py-3"
              >
                {stage.stage}
              </TabsTrigger>
            ))}
          </TabsList>

          {lifeStages.map((stage) => (
            <TabsContent key={stage.id} value={stage.id}>
              <Card className="overflow-hidden rounded-2xl shadow-lg border-4 border-amber-200/30">
                <div className="grid md:grid-cols-2 gap-0">
                  <div className="h-64 md:h-auto bg-gray-100">
                    <picture>
                      <source srcSet={stage.imageWebP} type="image/webp" />
                      <img
                        src={stage.imageJPG}
                        alt={stage.alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </picture>
                  </div>
                  <CardContent className="p-6 md:p-8 flex flex-col justify-center">
                    <h3 className="text-2xl font-serif font-medium mb-4 text-sage-800">
                      Chinese Medicine for {stage.stage}
                    </h3>
                    <p className="text-gray-700 mb-6">{stage.description}</p>
                    {stage.stage === "Teens" && (
                      <Button 
                        asChild
                        className="w-full sm:w-auto text-center break-words whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto min-w-0 items-stretch min-h-0"
                      >
                        <span className="block break-words whitespace-normal min-w-0 w-full">
                          <a href="/teens">
                            Learn More About Teen Health
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </a>
                        </span>
                      </Button>
                    )}
                    {stage.stage === "Young Adults" && (
                      <Button 
                        asChild
                        className="w-full sm:w-auto text-center break-words whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto min-w-0 items-stretch min-h-0"
                      >
                        <span className="block break-words whitespace-normal min-w-0 w-full">
                          <a href="/young-adults">
                            Learn More About Young Adults
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </a>
                        </span>
                      </Button>
                    )}
                    {stage.stage === "Pregnancy" && (
                      <Button 
                        asChild
                        className="w-full sm:w-auto text-center break-words whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto min-w-0 items-stretch min-h-0"
                      >
                        <span className="block break-words whitespace-normal min-w-0 w-full">
                          <a href="/pregnancy">
                            Learn More About Pregnancy
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </a>
                        </span>
                      </Button>
                    )}
                    {stage.stage === "Postpartum" && (
                      <Button 
                        asChild
                        className="w-full sm:w-auto text-center break-words whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto min-w-0 items-stretch min-h-0"
                      >
                        <span className="block break-words whitespace-normal min-w-0 w-full">
                          <a href="/postpartum">
                            Learn More About Postpartum
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </a>
                        </span>
                      </Button>
                    )}
                    {stage.stage === "Perimenopause" && (
                      <Button 
                        asChild
                        className="w-full sm:w-auto text-center break-words whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto min-w-0 items-stretch min-h-0"
                      >
                        <span className="block break-words whitespace-normal min-w-0 w-full">
                          <a href="/perimenopause">
                            Learn More About Perimenopause
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </a>
                        </span>
                      </Button>
                    )}
                    {stage.stage === "Menopause" && (
                      <Button 
                        asChild
                        className="w-full sm:w-auto text-center break-words whitespace-normal bg-sage-600 hover:bg-sage-700 text-white font-medium text-sm sm:text-base px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 mx-auto min-w-0 items-stretch min-h-0"
                      >
                        <span className="block break-words whitespace-normal min-w-0 w-full">
                          <a href="/menopause">
                            Learn More About Menopause
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </a>
                        </span>
                      </Button>
                    )}
                  </CardContent>
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};

export default LifeStageSection;
